import type { ReceiptDocument } from '@/lib/printing/types'
import { lira, methodLabel, money, receiptDateTime } from '@/lib/printing/format'

function escape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Renders the receipt as HTML sized for thermal paper.
 *
 * This is the path that goes through the browser's own print dialog and the OS printer driver,
 * which is the most reliable way to reach a USB printer on a PC.
 *
 * `@page { size: <width>mm auto }` is what makes the driver treat it as a receipt roll rather
 * than trying to fit it to A4.
 */
export function renderReceiptHtml(receipt: ReceiptDocument): string {
  const width = receipt.widthMm >= 80 ? 80 : 58
  // Printable area, allowing for the few millimetres a thermal head cannot reach.
  const contentWidth = width - 6

  const rows = receipt.lines
    .map((line) => {
      // A weighed line carries its unit; a counted one is a bare number, as it always was.
      const amount = Number.isInteger(line.quantity) ? line.quantity : line.quantity.toFixed(3)
      const qty = line.unit && line.priceUnit ? `${amount} ${line.unit}` : amount
      // "2500 g × $4.00/kg" — without the /kg the three figures on the line do not multiply.
      const rate = line.priceUnit ? `${money(line.unitPriceUsd)}/${line.priceUnit}` : money(line.unitPriceUsd)
      const discount =
        line.discountUsd > 0
          ? `<tr><td class="ind">Discount</td><td class="amt">-${money(line.discountUsd)}</td></tr>`
          : ''

      return `
        <tr><td colspan="2" class="name">${escape(line.name)}</td></tr>
        <tr>
          <td class="ind">${qty} × ${rate}</td>
          <td class="amt">${money(line.lineTotalUsd)}</td>
        </tr>
        ${discount}`
    })
    .join('')

  const vatRows = receipt.vatBands
    .filter((band) => band.vatUsd !== 0)
    .map(
      (band) =>
        `<tr><td>VAT ${(band.rate * 100).toFixed(0)}%</td><td class="amt">${money(band.vatUsd)}</td></tr>`,
    )
    .join('')

  const paymentRows = receipt.payments
    .map((payment) => {
      const shown =
        payment.method === 'CashLbp' ? `LL ${lira(payment.amountLbp)}` : money(payment.amountUsd)
      return `<tr><td>${escape(methodLabel(payment.method))}</td><td class="amt">${shown}</td></tr>`
    })
    .join('')

  const changeRows = [
    receipt.changeUsd > 0
      ? `<tr><td>Change</td><td class="amt">${money(receipt.changeUsd)}</td></tr>`
      : '',
    receipt.changeLbp > 0
      ? `<tr><td>Change</td><td class="amt">LL ${lira(receipt.changeLbp)}</td></tr>`
      : '',
  ].join('')

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escape(receipt.number)}</title>
<style>
  @page { size: ${width}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 3mm;
    width: ${contentWidth}mm;
    font-family: 'Courier New', ui-monospace, monospace;
    font-size: ${width >= 80 ? '9pt' : '8pt'};
    line-height: 1.35;
    color: #000;
    background: #fff;
    -webkit-print-color-adjust: exact;
  }
  .center { text-align: center; }
  .store { font-size: ${width >= 80 ? '14pt' : '12pt'}; font-weight: 700; }
  .branch { font-weight: 700; }
  .muted { font-size: 8pt; }
  hr { border: 0; border-top: 1px dashed #000; margin: 2mm 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 0; vertical-align: top; }
  .amt { text-align: right; white-space: nowrap; padding-left: 2mm; }
  .name { padding-top: 1mm; }
  .ind { padding-left: 3mm; }
  .total td { font-size: ${width >= 80 ? '13pt' : '11pt'}; font-weight: 700; padding-top: 1mm; }
  .reprint { font-weight: 700; text-align: center; }
</style>
</head>
<body>
  <div class="center">
    <div class="store">${escape(receipt.storeName)}</div>
    ${
      // Always shown, smaller, directly under the store name — a branch never stands alone.
      receipt.branchName ? `<div class="branch">${escape(receipt.branchName)}</div>` : ''
    }
    ${receipt.header && receipt.header !== receipt.storeName ? `<div class="muted">${escape(receipt.header)}</div>` : ''}
    ${receipt.vatNumber ? `<div class="muted">VAT ${escape(receipt.vatNumber)}</div>` : ''}
  </div>

  <hr>
  <table>
    <tr><td>${escape(receipt.number)}</td><td class="amt">${receiptDateTime(receipt.completedAtUtc)}</td></tr>
    <tr><td colspan="2">Cashier: ${escape(receipt.cashierName)}</td></tr>
    ${receipt.customerName ? `<tr><td colspan="2">Customer: ${escape(receipt.customerName)}</td></tr>` : ''}
  </table>
  ${receipt.reprintCount > 0 ? `<div class="reprint">*** REPRINT #${receipt.reprintCount} ***</div>` : ''}

  <hr>
  <table>${rows}</table>

  <hr>
  <table>
    <tr><td>Subtotal</td><td class="amt">${money(receipt.subtotalUsd)}</td></tr>
    ${receipt.discountUsd > 0 ? `<tr><td>Discount</td><td class="amt">-${money(receipt.discountUsd)}</td></tr>` : ''}
    ${vatRows}
    <tr class="total"><td>TOTAL</td><td class="amt">$${money(receipt.totalUsd)}</td></tr>
    <tr><td></td><td class="amt">LL ${lira(receipt.totalLbp)}</td></tr>
  </table>
  <div class="muted">Rate: 1 USD = ${lira(receipt.lbpRateUsed)} LL</div>

  <hr>
  <table>${paymentRows}${changeRows}</table>

  ${
    // The part of a credit sale the customer will want to argue about later, printed while the
    // figures are still the ones that applied. The balance is the one at the time of the sale,
    // so a reprint does not quietly restate it.
    receipt.accountChargedUsd > 0
      ? `<hr>
  <table>
    <tr class="total"><td>ON ACCOUNT</td><td class="amt">$${money(receipt.accountChargedUsd)}</td></tr>
    ${
      receipt.accountBalanceAfterUsd !== null
        ? `<tr><td>Balance owing</td><td class="amt">$${money(receipt.accountBalanceAfterUsd)}</td></tr>`
        : ''
    }
    <tr><td colspan="2" class="muted">Not paid — charged to the account above.</td></tr>
  </table>`
      : ''
  }

  ${receipt.pricesIncludeVat ? '<hr><div class="center muted">Prices include VAT</div>' : ''}
  ${receipt.footer ? `<div class="center" style="margin-top:2mm">${escape(receipt.footer)}</div>` : ''}
</body>
</html>`
}
